export interface ChecklistTask {
  id: string;
  titleKey: string;
  descriptionKey?: string;
  exampleKeys?: string[];
  instructionChart?: 'clarity' | 'contentType' | 'youtubeImpact' | 'trafficSources';
}

export interface ChecklistSection {
  id: string;
  titleKey: string;
  subtitleKey?: string;
  icon?: string;
  tasks: ChecklistTask[];
}

export interface KeyTakeaway {
  titleKey: string;
  descriptionKey: string;
  sectionId?: string;
}

export interface ChecklistTab {
  type: 'TOPICS_PROMPTS' | 'AI_VISIBILITY';
  labelKey: string;
  slug: string;
  hidden?: boolean;
  introTitleKey: string;
  introSubtitleKey: string;
  introDescKey: string;
  introHelperKey: string;
  introBulletKeys: string[];
  introFooterKey: string;
  introKeyTakeaways?: KeyTakeaway[];
  introFooter2Key?: string;
  showChart?: boolean;
  sections: ChecklistSection[];
}

export const CHECKLIST_TABS: ChecklistTab[] = [
  {
    type: 'TOPICS_PROMPTS',
    labelKey: 'geoChecklist.tabs.topicsPrompts',
    slug: 'topics-prompts',
    hidden: true,
    introTitleKey: 'geoChecklist.tp.introTitle',
    introSubtitleKey: 'geoChecklist.tp.introSubtitle',
    introDescKey: 'geoChecklist.tp.introDesc',
    introHelperKey: 'geoChecklist.tp.introHelper',
    introBulletKeys: [
      'geoChecklist.tp.introBullet1',
      'geoChecklist.tp.introBullet2',
      'geoChecklist.tp.introBullet3',
      'geoChecklist.tp.introBullet4',
    ],
    introFooterKey: 'geoChecklist.tp.introFooter',
    sections: [
      {
        id: 'tp_1',
        titleKey: 'geoChecklist.section.coreTopics',
        tasks: [
          {
            id: 'tp_1_1',
            titleKey: 'geoChecklist.task.tp_1_1.title',
          },
          {
            id: 'tp_1_2',
            titleKey: 'geoChecklist.task.tp_1_2.title',
            exampleKeys: [
              'geoChecklist.task.tp_1_2.example1',
              'geoChecklist.task.tp_1_2.example2',
              'geoChecklist.task.tp_1_2.example3',
            ],
          },
          {
            id: 'tp_1_3',
            titleKey: 'geoChecklist.task.tp_1_3.title',
            exampleKeys: [
              'geoChecklist.task.tp_1_3.example1',
              'geoChecklist.task.tp_1_3.example2',
              'geoChecklist.task.tp_1_3.example3',
            ],
          },
          {
            id: 'tp_1_4',
            titleKey: 'geoChecklist.task.tp_1_4.title',
            exampleKeys: [
              'geoChecklist.task.tp_1_4.example1',
              'geoChecklist.task.tp_1_4.example2',
              'geoChecklist.task.tp_1_4.example3',
            ],
          },
          {
            id: 'tp_1_5',
            titleKey: 'geoChecklist.task.tp_1_5.title',
            exampleKeys: [
              'geoChecklist.task.tp_1_5.example1',
              'geoChecklist.task.tp_1_5.example2',
              'geoChecklist.task.tp_1_5.example3',
            ],
          },
          {
            id: 'tp_1_6',
            titleKey: 'geoChecklist.task.tp_1_6.title',
          },
          {
            id: 'tp_1_7',
            titleKey: 'geoChecklist.task.tp_1_7.title',
          },
        ],
      },
      {
        id: 'tp_2',
        titleKey: 'geoChecklist.section.promptGeneration',
        tasks: [
          {
            id: 'tp_2_1',
            titleKey: 'geoChecklist.task.tp_2_1.title',
          },
          {
            id: 'tp_2_2',
            titleKey: 'geoChecklist.task.tp_2_2.title',
            exampleKeys: [
              'geoChecklist.task.tp_2_2.example1',
              'geoChecklist.task.tp_2_2.example2',
              'geoChecklist.task.tp_2_2.example3',
              'geoChecklist.task.tp_2_2.example4',
            ],
          },
          {
            id: 'tp_2_3',
            titleKey: 'geoChecklist.task.tp_2_3.title',
          },
          {
            id: 'tp_2_4',
            titleKey: 'geoChecklist.task.tp_2_4.title',
            descriptionKey: 'geoChecklist.task.tp_2_4.description',
          },
          {
            id: 'tp_2_5',
            titleKey: 'geoChecklist.task.tp_2_5.title',
          },
        ],
      },
      {
        id: 'tp_3',
        titleKey: 'geoChecklist.section.systemUpload',
        tasks: [
          {
            id: 'tp_3_1',
            titleKey: 'geoChecklist.task.tp_3_1.title',
          },
          {
            id: 'tp_3_2',
            titleKey: 'geoChecklist.task.tp_3_2.title',
          },
        ],
      },
      {
        id: 'tp_4',
        titleKey: 'geoChecklist.section.bestPractices',
        tasks: [
          {
            id: 'tp_4_1',
            titleKey: 'geoChecklist.task.tp_4_1.title',
            descriptionKey: 'geoChecklist.task.tp_4_1.description',
          },
          {
            id: 'tp_4_2',
            titleKey: 'geoChecklist.task.tp_4_2.title',
            descriptionKey: 'geoChecklist.task.tp_4_2.description',
          },
          {
            id: 'tp_4_3',
            titleKey: 'geoChecklist.task.tp_4_3.title',
            descriptionKey: 'geoChecklist.task.tp_4_3.description',
          },
        ],
      },
      {
        id: 'tp_5',
        titleKey: 'geoChecklist.section.evaluationMaintenance',
        tasks: [
          {
            id: 'tp_5_1',
            titleKey: 'geoChecklist.task.tp_5_1.title',
          },
          {
            id: 'tp_5_2',
            titleKey: 'geoChecklist.task.tp_5_2.title',
          },
          {
            id: 'tp_5_3',
            titleKey: 'geoChecklist.task.tp_5_3.title',
          },
        ],
      },
    ],
  },
  {
    type: 'AI_VISIBILITY',
    labelKey: 'geoChecklist.tabs.aiVisibility',
    slug: 'ai-visibility',
    introTitleKey: 'geoChecklist.av.introTitle',
    introSubtitleKey: 'geoChecklist.av.introSubtitle',
    introDescKey: 'geoChecklist.av.introDesc',
    introHelperKey: 'geoChecklist.av.introHelper',
    introBulletKeys: [],
    introFooterKey: 'geoChecklist.av.introFooter',
    introFooter2Key: 'geoChecklist.av.introFooter2',
    showChart: true,
    introKeyTakeaways: [
      { titleKey: 'geoChecklist.av.kt1.title', descriptionKey: 'geoChecklist.av.kt1.desc', sectionId: 'av_1' },
      { titleKey: 'geoChecklist.av.kt2.title', descriptionKey: 'geoChecklist.av.kt2.desc', sectionId: 'av_3' },
      { titleKey: 'geoChecklist.av.kt3.title', descriptionKey: 'geoChecklist.av.kt3.desc', sectionId: 'av_1' },
      { titleKey: 'geoChecklist.av.kt4.title', descriptionKey: 'geoChecklist.av.kt4.desc', sectionId: 'av_4' },
      { titleKey: 'geoChecklist.av.kt5.title', descriptionKey: 'geoChecklist.av.kt5.desc', sectionId: 'av_2' },
      { titleKey: 'geoChecklist.av.kt6.title', descriptionKey: 'geoChecklist.av.kt6.desc', sectionId: 'av_3' },
      { titleKey: 'geoChecklist.av.kt7.title', descriptionKey: 'geoChecklist.av.kt7.desc', sectionId: 'av_4' },
      { titleKey: 'geoChecklist.av.kt8.title', descriptionKey: 'geoChecklist.av.kt8.desc', sectionId: 'av_2' },
      { titleKey: 'geoChecklist.av.kt9.title', descriptionKey: 'geoChecklist.av.kt9.desc', sectionId: 'av_1' },
      { titleKey: 'geoChecklist.av.kt10.title', descriptionKey: 'geoChecklist.av.kt10.desc', sectionId: 'av_3' },
      { titleKey: 'geoChecklist.av.kt11.title', descriptionKey: 'geoChecklist.av.kt11.desc', sectionId: 'av_4' },
      { titleKey: 'geoChecklist.av.kt12.title', descriptionKey: 'geoChecklist.av.kt12.desc', sectionId: 'av_6' },
      { titleKey: 'geoChecklist.av.kt13.title', descriptionKey: 'geoChecklist.av.kt13.desc', sectionId: 'av_1' },
      { titleKey: 'geoChecklist.av.kt14.title', descriptionKey: 'geoChecklist.av.kt14.desc', sectionId: 'av_3' },
      { titleKey: 'geoChecklist.av.kt15.title', descriptionKey: 'geoChecklist.av.kt15.desc', sectionId: 'av_6' },
      { titleKey: 'geoChecklist.av.kt16.title', descriptionKey: 'geoChecklist.av.kt16.desc', sectionId: 'av_2' },
      { titleKey: 'geoChecklist.av.kt17.title', descriptionKey: 'geoChecklist.av.kt17.desc', sectionId: 'av_5' },
    ],
    sections: [
      {
        id: 'av_1',
        titleKey: 'geoChecklist.section.accessDiscoverability',
        subtitleKey: 'geoChecklist.section.accessDiscoverabilitySubtitle',
        icon: 'globe',
        tasks: [
          { id: 'av_1_1', titleKey: 'geoChecklist.task.av_1_1.title', descriptionKey: 'geoChecklist.task.av_1_1.description', instructionChart: 'clarity' },
          { id: 'av_1_2', titleKey: 'geoChecklist.task.av_1_2.title', descriptionKey: 'geoChecklist.task.av_1_2.description' },
          { id: 'av_1_3', titleKey: 'geoChecklist.task.av_1_3.title', descriptionKey: 'geoChecklist.task.av_1_3.description' },
          { id: 'av_1_4', titleKey: 'geoChecklist.task.av_1_4.title', descriptionKey: 'geoChecklist.task.av_1_4.description', instructionChart: 'contentType' },
          { id: 'av_1_5', titleKey: 'geoChecklist.task.av_1_5.title', descriptionKey: 'geoChecklist.task.av_1_5.description' },
          { id: 'av_1_6', titleKey: 'geoChecklist.task.av_1_6.title', descriptionKey: 'geoChecklist.task.av_1_6.description' },
          { id: 'av_1_7', titleKey: 'geoChecklist.task.av_1_7.title', descriptionKey: 'geoChecklist.task.av_1_7.description' },
        ],
      },
      {
        id: 'av_2',
        titleKey: 'geoChecklist.section.contentAiUnderstanding',
        subtitleKey: 'geoChecklist.section.contentAiUnderstandingSubtitle',
        icon: 'edit',
        tasks: [
          { id: 'av_2_1', titleKey: 'geoChecklist.task.av_2_1.title', descriptionKey: 'geoChecklist.task.av_2_1.description' },
          { id: 'av_2_2', titleKey: 'geoChecklist.task.av_2_2.title', descriptionKey: 'geoChecklist.task.av_2_2.description' },
          { id: 'av_2_3', titleKey: 'geoChecklist.task.av_2_3.title', descriptionKey: 'geoChecklist.task.av_2_3.description' },
          { id: 'av_2_4', titleKey: 'geoChecklist.task.av_2_4.title', descriptionKey: 'geoChecklist.task.av_2_4.description' },
          { id: 'av_2_5', titleKey: 'geoChecklist.task.av_2_5.title', descriptionKey: 'geoChecklist.task.av_2_5.description' },
          { id: 'av_2_6', titleKey: 'geoChecklist.task.av_2_6.title', descriptionKey: 'geoChecklist.task.av_2_6.description' },
          { id: 'av_2_7', titleKey: 'geoChecklist.task.av_2_7.title', descriptionKey: 'geoChecklist.task.av_2_7.description' },
          { id: 'av_2_8', titleKey: 'geoChecklist.task.av_2_8.title', descriptionKey: 'geoChecklist.task.av_2_8.description' },
        ],
      },
      {
        id: 'av_3',
        titleKey: 'geoChecklist.section.brandSignals',
        subtitleKey: 'geoChecklist.section.brandSignalsSubtitle',
        icon: 'star',
        tasks: [
          { id: 'av_3_1', titleKey: 'geoChecklist.task.av_3_1.title', descriptionKey: 'geoChecklist.task.av_3_1.description' },
          { id: 'av_3_2', titleKey: 'geoChecklist.task.av_3_2.title', descriptionKey: 'geoChecklist.task.av_3_2.description' },
          { id: 'av_3_3', titleKey: 'geoChecklist.task.av_3_3.title', descriptionKey: 'geoChecklist.task.av_3_3.description' },
          { id: 'av_3_4', titleKey: 'geoChecklist.task.av_3_4.title', descriptionKey: 'geoChecklist.task.av_3_4.description' },
          { id: 'av_3_5', titleKey: 'geoChecklist.task.av_3_5.title', descriptionKey: 'geoChecklist.task.av_3_5.description' },
          { id: 'av_3_6', titleKey: 'geoChecklist.task.av_3_6.title', descriptionKey: 'geoChecklist.task.av_3_6.description' },
        ],
      },
      {
        id: 'av_4',
        titleKey: 'geoChecklist.section.distributionCommunity',
        subtitleKey: 'geoChecklist.section.distributionCommunitySubtitle',
        icon: 'chat',
        tasks: [
          { id: 'av_4_1', titleKey: 'geoChecklist.task.av_4_1.title', descriptionKey: 'geoChecklist.task.av_4_1.description' },
          { id: 'av_4_2', titleKey: 'geoChecklist.task.av_4_2.title', descriptionKey: 'geoChecklist.task.av_4_2.description' },
          { id: 'av_4_3', titleKey: 'geoChecklist.task.av_4_3.title', descriptionKey: 'geoChecklist.task.av_4_3.description' },
          { id: 'av_4_4', titleKey: 'geoChecklist.task.av_4_4.title', descriptionKey: 'geoChecklist.task.av_4_4.description', instructionChart: 'youtubeImpact' },
        ],
      },
      {
        id: 'av_5',
        titleKey: 'geoChecklist.section.monitoringCompetitive',
        subtitleKey: 'geoChecklist.section.monitoringCompetitiveSubtitle',
        icon: 'chart',
        tasks: [
          { id: 'av_5_1', titleKey: 'geoChecklist.task.av_5_1.title', descriptionKey: 'geoChecklist.task.av_5_1.description' },
          { id: 'av_5_2', titleKey: 'geoChecklist.task.av_5_2.title', descriptionKey: 'geoChecklist.task.av_5_2.description' },
          { id: 'av_5_3', titleKey: 'geoChecklist.task.av_5_3.title', descriptionKey: 'geoChecklist.task.av_5_3.description' },
          { id: 'av_5_4', titleKey: 'geoChecklist.task.av_5_4.title', descriptionKey: 'geoChecklist.task.av_5_4.description' },
          { id: 'av_5_5', titleKey: 'geoChecklist.task.av_5_5.title', descriptionKey: 'geoChecklist.task.av_5_5.description', instructionChart: 'trafficSources' },
          { id: 'av_5_6', titleKey: 'geoChecklist.task.av_5_6.title', descriptionKey: 'geoChecklist.task.av_5_6.description' },
          { id: 'av_5_7', titleKey: 'geoChecklist.task.av_5_7.title', descriptionKey: 'geoChecklist.task.av_5_7.description' },
          { id: 'av_5_8', titleKey: 'geoChecklist.task.av_5_8.title', descriptionKey: 'geoChecklist.task.av_5_8.description' },
          { id: 'av_5_9', titleKey: 'geoChecklist.task.av_5_9.title', descriptionKey: 'geoChecklist.task.av_5_9.description' },
        ],
      },
      {
        id: 'av_6',
        titleKey: 'geoChecklist.section.ownedAiSurfaces',
        subtitleKey: 'geoChecklist.section.ownedAiSurfacesSubtitle',
        icon: 'zap',
        tasks: [
          { id: 'av_6_1', titleKey: 'geoChecklist.task.av_6_1.title', descriptionKey: 'geoChecklist.task.av_6_1.description' },
          { id: 'av_6_2', titleKey: 'geoChecklist.task.av_6_2.title', descriptionKey: 'geoChecklist.task.av_6_2.description' },
          { id: 'av_6_3', titleKey: 'geoChecklist.task.av_6_3.title', descriptionKey: 'geoChecklist.task.av_6_3.description' },
        ],
      },
    ],
  },
];
